/**
 * Nearby cities dropdown component
 * Shows location-based or popular cities on focus
 */

import { t } from '../../i18n.js';

export function createNearbyDropdown($field, options = {}) {
    const {
        getCoordinates = () => ({ lat: null, lon: null }),
        onCitySelect = () => {},
        isHomeCity = false
    } = options;

    let $dropdown;

    // Create dropdown element
    function initDropdown() {
        // Wrap field if not already wrapped
        if (!$field.parent().hasClass('nearby-dropdown-container')) {
            const $container = $('<div class="nearby-dropdown-container" style="position: relative; width: 100%;"></div>');
            $field.wrap($container);
        }

        // Create or find dropdown
        $dropdown = $field.siblings('.nearby-dropdown');
        if ($dropdown.length === 0) {
            $dropdown = $(`<div class="nearby-dropdown" style="
                display: none;
                position: absolute;
                top: 100%;
                left: 0;
                right: 0;
                background: white;
                border: 1px solid #e5e7eb;
                border-radius: 0.75rem;
                margin-top: 4px;
                max-height: 300px;
                overflow-y: auto;
                z-index: 999999;
                box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
            "></div>`);
            $field.after($dropdown);
        }

        return $dropdown;
    }

    // Show loading state
    function showLoading() {
        $dropdown.empty().append(`
            <div style="padding: 20px; text-align: center; color: #6b7280;">
                <i class="fa fa-spinner fa-spin" style="margin-right: 8px;"></i>
                ${t('auth.finding_nearby_cities')}
            </div>
        `);
        $dropdown.fadeIn(200);
    }

    // Show waiting state
    function showWaiting() {
        $dropdown.empty().append(`
            <div style="padding: 20px; text-align: center; color: #6b7280;">
                <i class="fa fa-spinner fa-spin" style="margin-right: 8px;"></i>
                ${t('auth.getting_your_location')}
            </div>
        `);
        $dropdown.fadeIn(200);
    }

    // Show error state
    function showError(message = null) {
        const errorMsg = message || t('auth.error_loading_cities');
        $dropdown.empty().append(`
            <div style="padding: 20px; text-align: center; color: #ef4444;">
                <i class="fa fa-exclamation-circle" style="margin-right: 8px;"></i>
                ${errorMsg}
            </div>
        `);
        setTimeout(() => $dropdown.fadeOut(200), 2000);
    }

    // Fetch and display nearby cities
    async function loadNearbyCities() {
        const { lat, lon } = getCoordinates();

        // Only show dropdown if we have coordinates
        if (!lat || !lon) {
            // No coordinates available - don't show anything, let user type
            return;
        }

        showLoading();

        // Build API URL with location
        const url = `/v1/search/geo?type=city&limit=10&location=${lat},${lon}&sort=distance`;

        // Get locale from i18n config for Accept-Language header
        const locale = window.__i18nConfig?.locale || window.I18n?.getLocale() || 'en_US';

        try {
            const response = await fetch(url, {
                headers: {
                    'Accept-Language': locale.replace('_', '-')
                }
            });
            const data = await response.json();

            if (data.data && data.data.length > 0) {
                displayCities(data.data, true);
            } else {
                showError(t('auth.no_nearby_cities'));
            }
        } catch (error) {
            console.error('[Dropdown] Error fetching cities:', error);
            showError();
        }
    }

    // Display cities in dropdown
    function displayCities(cities, hasLocation) {
        $dropdown.empty();

        // Add city items
        cities.forEach((city, index) => {
            const cityName = formatCityName(city);
            const countryIso = city.country_iso || '';
            const flagIcon = `/images/circle-flags/flags/${countryIso}.svg`;

            const $item = $(`
                <div class="nearby-city-item" style="
                    display: flex;
                    align-items: center;
                    padding: 10px 14px;
                    cursor: pointer;
                    transition: background-color 0.15s ease;
                " data-city-id="${city.id}" data-city-name="${cityName}" data-country-iso="${countryIso}">
                    <img src="${flagIcon}" style="width: 20px; height: 20px; margin-right: 10px; border-radius: 50%;">
                    <span>${cityName}</span>
                    ${index === 0 && hasLocation ? `<span style="margin-left: auto; font-size: 11px; color: #10b981;">${t('auth.nearest')}</span>` : ''}
                </div>
            `);

            // Hover effect
            $item.hover(
                function() { $(this).css('background-color', '#f0f9ff'); },
                function() { $(this).css('background-color', ''); }
            );

            // Click handler
            $item.on('click', function() {
                const cityId = $(this).data('city-id');
                const cityName = $(this).data('city-name');
                const countryIso = $(this).data('country-iso');
                selectCity(cityId, cityName, flagIcon, countryIso);
            });

            $dropdown.append($item);
        });
    }

    // Format city name with region and country
    function formatCityName(city) {
        let name = city.name;
        if (city.parent_names) {
            if (city.parent_names.region) {
                name += ', ' + city.parent_names.region;
            }
            name += ', ' + city.parent_names.country;
        }
        return name;
    }

    // Handle city selection
    function selectCity(cityId, cityName, flagIcon, countryIso) {
        // Update text input
        $field.val(cityName);

        // Update hidden field
        const $hiddenField = $field.siblings('.cityVal');
        if ($hiddenField.length) {
            $hiddenField.val(cityId).trigger('change');
            $hiddenField[0].dispatchEvent(new Event('input', { bubbles: true }));
        }

        // Call external handler with countryIso
        onCitySelect(cityId, cityName, countryIso);

        // Hide dropdown
        $dropdown.fadeOut(200);

        // Store and add flag to input
        $field.data('selected-flag', flagIcon);
        $field.data('selected-country-iso', countryIso);
        $field.css({
            'transition': 'background-color 0.3s ease',
            'background-color': 'rgba(40, 174, 240, 0.1)',
            'background-image': `url("${flagIcon}")`,
            'background-repeat': 'no-repeat',
            'background-position': 'left 12px center',
            'background-size': '20px 20px',
            'padding-left': '40px'
        });
        setTimeout(() => $field.css('background-color', ''), 300);

        // Hide location icon when flag is shown
        const $container = $field.closest('.easy-autocomplete');
        $container.find('.location-icon').hide();
    }

    // Hide dropdown
    function hide() {
        $dropdown.fadeOut(200);
    }

    // Public API
    return {
        init: initDropdown,
        show: loadNearbyCities,
        showWaiting,
        hide,
        destroy() {
            $dropdown.remove();
            if ($field.parent().hasClass('nearby-dropdown-container')) {
                $field.unwrap();
            }
        }
    };
}
